import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow
} from "react-flow-renderer";

import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Paper,
  Drawer,
  TextField,
  MenuItem,
  Divider,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Chip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import PlayArrowOutlinedIcon from "@material-ui/icons/PlayArrowOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import CloudDownloadOutlinedIcon from "@material-ui/icons/CloudDownloadOutlined";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import LibraryBooksOutlinedIcon from "@material-ui/icons/LibraryBooksOutlined";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useConfirm from "../../hooks/useConfirm";

const NODE_CATEGORIES = [
  { key: "flow", label: "Fluxo" },
  { key: "messages", label: "Mensagens" },
  { key: "conditions", label: "Condicoes" },
  { key: "attendance", label: "Atendimento" },
  { key: "ai", label: "IA" },
  { key: "integrations", label: "Integracoes" },
  { key: "reports", label: "Relatorios" }
];

const NODE_DEFINITIONS = [
  // Fluxo
  { category: "flow", type: "start", label: "Inicio", color: "#1565c0", description: "Ponto de partida do fluxo." },
  { category: "flow", type: "end", label: "Fim", color: "#424242", description: "Encerra o fluxo." },
  { category: "flow", type: "subflow", label: "Subfluxo", color: "#455a64", description: "Termina o fluxo atual; o flow alvo precisa estar configurado pra assumir via queueId/default." },
  { category: "flow", type: "wait", label: "Aguardar", color: "#5d4037", description: "Pausa N ms antes de continuar." },
  { category: "flow", type: "business_hours", label: "Horario atendimento", color: "#37474f", description: "Bifurca em open/closed conforme dia+hora." },

  // Mensagens
  { category: "messages", type: "send_message", label: "Enviar mensagem", color: "#2e7d32", description: "Envia texto ao cliente." },
  { category: "messages", type: "send_media", label: "Enviar midia", color: "#388e3c", description: "Envia imagem/audio/video (com caption)." },
  { category: "messages", type: "ask", label: "Perguntar e aguardar", color: "#0277bd", description: "Envia pergunta e PAUSA esperando resposta." },
  { category: "messages", type: "send_options", label: "Enviar opcoes", color: "#0288d1", description: "Envia mensagem com opcoes numeradas e PAUSA aguardando resposta." },
  { category: "messages", type: "menu_interactive", label: "Menu interativo", color: "#0097a7", description: "Igual a 'Enviar opcoes' (botoes interativos WhatsApp nao suportados via wwebjs)." },

  // Condicoes
  { category: "conditions", type: "condition", label: "Condicao (true/false)", color: "#ef6c00", description: "Duas saidas: true e false." },
  { category: "conditions", type: "switch", label: "Switch (N saidas)", color: "#d84315", description: "N saidas conforme primeira opcao que casa." },
  { category: "conditions", type: "check_variable", label: "Verificar variavel", color: "#e65100", description: "Compara valor de vars.NOME." },
  { category: "conditions", type: "validate_response", label: "Validar resposta", color: "#bf360c", description: "Regex valid/invalid." },
  { category: "conditions", type: "timeout_response", label: "Timeout de resposta", color: "#4e342e", description: "Marca janela de timeout em vars (combine com cron externo p/ fallback)." },
  { category: "conditions", type: "retry_limit", label: "Limite de tentativas", color: "#3e2723", description: "Incrementa contador; saidas: ok / exceeded." },

  // Atendimento
  { category: "attendance", type: "transfer_queue", label: "Transferir fila", color: "#f57c00", description: "Move ticket para outra fila." },
  { category: "attendance", type: "transfer_human", label: "Transferir humano", color: "#c62828", description: "Encerra automacao." },
  { category: "attendance", type: "check_queue", label: "Verificar fila", color: "#ef5350", description: "Bifurca open/closed conforme Queue.aiEnabled." },
  { category: "attendance", type: "check_agent_online", label: "Verificar atendente", color: "#e57373", description: "Atendente ativo nos ultimos 15min? Saidas: yes / no." },
  { category: "attendance", type: "pause_bot", label: "Pausar bot", color: "#b71c1c", description: "Desativa Bot/IA para este cliente." },
  { category: "attendance", type: "resume_bot", label: "Reativar bot", color: "#1b5e20", description: "Reativa Bot/IA para este cliente." },
  { category: "attendance", type: "apply_tag", label: "Aplicar tag", color: "#00838f", description: "Aplica tag ao cliente." },

  // IA
  { category: "ai", type: "ai_response", label: "Responder com IA", color: "#6a1b9a", description: "Chama o provedor de IA pra responder." },
  { category: "ai", type: "classify_ai", label: "Classificar com IA", color: "#7b1fa2", description: "IA escolhe entre intencoes pre-definidas (handle = intencao)." },
  { category: "ai", type: "extract_data_ai", label: "Extrair dados com IA", color: "#8e24aa", description: "OpenAI extrai JSON conforme schema. Saidas: success / error." },
  { category: "ai", type: "summarize_ai", label: "Resumir conversa", color: "#9c27b0", description: "IA resume historico (vars.summary)." },

  // Integracoes
  { category: "integrations", type: "http_request", label: "Requisicao HTTP", color: "#283593", description: "Chama URL externa. Saidas: success/error." },
  { category: "integrations", type: "query_data", label: "Consultar dados (SQL)", color: "#303f9f", description: "Query no banco interno via Sequelize (whitelist: client, contact, ticket, deal, ...). Saidas: found / not_found." },
  { category: "integrations", type: "generate_payment", label: "Gerar pagamento", color: "#1976d2", description: "Cria PaymentCharge (gateway + valor). Saidas: success / error." },
  { category: "integrations", type: "check_payment", label: "Verificar pagamento", color: "#1565c0", description: "Le status. Saidas: paid / pending / failed / expired / other / error." },
  { category: "integrations", type: "send_email", label: "Enviar e-mail", color: "#0d47a1", description: "Registra rascunho em AuditLog (SMTP nao configurado ainda)." },
  { category: "integrations", type: "create_event", label: "Criar evento", color: "#01579b", description: "Cria Appointment vinculado ao cliente/ticket." },
  { category: "integrations", type: "notify_team", label: "Notificar equipe", color: "#0277bd", description: "Broadcast via socket.io + AuditLog." },

  // Relatorios
  { category: "reports", type: "log_event", label: "Registrar evento", color: "#558b2f", description: "Grava no AuditLog." },
  { category: "reports", type: "nps_survey", label: "Pesquisa NPS", color: "#33691e", description: "Pergunta nota 0-10. Saidas: promoter / passive / detractor / invalid." }
];

const NODE_DEF_MAP = NODE_DEFINITIONS.reduce((acc, d) => {
  acc[d.type] = d;
  return acc;
}, {});

// nodeKey precisa ser unico dentro do fluxo; nao precisa ser UUID global.
const makeNodeKey = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Custom node pro tipo "switch" — renderiza N handles de saida (um
 * por opcao em data.configJson.options) + 1 handle "default". O
 * sourceHandle de cada edge sera "output_<index>" ou "default",
 * batendo com a logica do FlowNodeProcessor no backend.
 */
const SwitchNode = ({ data, selected }) => {
  const cfg = data?.configJson || {};
  const options = Array.isArray(cfg.options) ? cfg.options : [];
  const def = NODE_DEF_MAP.switch;
  const color = def?.color || "#d84315";
  const minWidth = Math.max(180, 80 + options.length * 50);

  return (
    <div
      style={{
        border: `2px solid ${selected ? "#1976d2" : color}`,
        background: "#fff",
        borderRadius: 8,
        padding: "8px 12px 22px",
        fontSize: 13,
        minWidth,
        color: "#333",
        position: "relative"
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="in"
        style={{ background: "#555" }}
      />
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
        {data?.label || "Switch"}
      </div>
      <div style={{ fontSize: 10, color: "#888" }}>
        {options.length} {options.length === 1 ? "saida" : "saidas"} + default
      </div>

      {/* Labels das saidas (visual apenas) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          marginTop: 8,
          gap: 4,
          fontSize: 9,
          color: "#666"
        }}
      >
        {options.map((opt, i) => (
          <div
            key={`label_${i}`}
            style={{
              flex: 1,
              textAlign: "center",
              borderTop: `2px solid ${color}`,
              paddingTop: 2,
              maxWidth: 70,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
            title={opt.label || `Opção ${i + 1}`}
          >
            {opt.label || `Opc ${i + 1}`}
          </div>
        ))}
        <div
          style={{
            flex: 1,
            textAlign: "center",
            borderTop: "2px dashed #999",
            paddingTop: 2,
            maxWidth: 70,
            color: "#999"
          }}
        >
          default
        </div>
      </div>

      {/* Handles dinamicos — distribuidos igualmente no rodape do node */}
      {options.map((_opt, i) => {
        const total = options.length + 1; // +1 pelo default
        const pct = ((i + 0.5) / total) * 100;
        return (
          <Handle
            key={`output_${i}`}
            type="source"
            position={Position.Bottom}
            id={`output_${i}`}
            style={{
              left: `${pct}%`,
              background: color,
              width: 8,
              height: 8
            }}
          />
        );
      })}
      <Handle
        type="source"
        position={Position.Bottom}
        id="default"
        style={{
          left: `${((options.length + 0.5) / (options.length + 1)) * 100}%`,
          background: "#999",
          width: 8,
          height: 8
        }}
      />
    </div>
  );
};

/**
 * Custom node pro tipo "condition" — duas saidas independentes:
 * TRUE (verde, esquerda) e FALSE (vermelho, direita). Os handles tem
 * id="true" e id="false" que viram sourceHandle no edge. O backend
 * (FlowNodeProcessor) ja escolhe entre eles via findOutgoing(.., "true"/"false").
 */
const ConditionNode = ({ data, selected }) => {
  const def = NODE_DEF_MAP.condition;
  const color = def?.color || "#ef6c00";
  return (
    <div
      style={{
        border: `2px solid ${selected ? "#1976d2" : color}`,
        background: "#fff",
        borderRadius: 8,
        padding: "8px 12px 22px",
        fontSize: 13,
        minWidth: 160,
        color: "#333",
        position: "relative"
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="in"
        style={{ background: "#555" }}
      />
      <div style={{ fontWeight: 600, fontSize: 12 }}>
        {data?.label || "Condicao"}
      </div>
      <div style={{ fontSize: 10, color: "#888" }}>true / false</div>

      {/* Labels das saidas */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 8,
          fontSize: 9,
          fontWeight: 700
        }}
      >
        <div
          style={{
            color: "#2e7d32",
            borderTop: "2px solid #2e7d32",
            paddingTop: 2,
            width: 50,
            textAlign: "center"
          }}
        >
          TRUE
        </div>
        <div
          style={{
            color: "#c62828",
            borderTop: "2px solid #c62828",
            paddingTop: 2,
            width: 50,
            textAlign: "center"
          }}
        >
          FALSE
        </div>
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        style={{
          left: "25%",
          background: "#2e7d32",
          width: 10,
          height: 10
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        style={{
          left: "75%",
          background: "#c62828",
          width: 10,
          height: 10
        }}
      />
    </div>
  );
};

const NODE_TYPES = {
  switch: SwitchNode,
  condition: ConditionNode
};

// Mapa por tipo de node das chaves de configuracao que a UI conhece.
// Tudo que cair fora desse allow-list e descartado no load e no save
// — garante que lixo (imagem base64 colada, campos legados, etc.) nao
// fique preso em configJson invisivel e infle o payload do save.
const CONFIG_ALLOWED_KEYS = {
  // Fluxo
  start: [],
  end: [],
  subflow: ["targetFlowId"],
  wait: ["delayMs"],
  business_hours: ["startHour", "endHour", "days"],
  // Mensagens
  send_message: ["text"],
  send_media: ["mediaUrl", "mediaType", "caption"],
  ask: ["question", "storeAs"],
  send_options: ["text", "options", "storeAs"],
  menu_interactive: ["text", "options", "storeAs"],
  // Condicoes
  condition: ["kind", "value", "source"],
  switch: ["source", "options"],
  check_variable: ["varName", "kind", "value"],
  validate_response: ["pattern", "source"],
  timeout_response: ["timeoutMs"],
  retry_limit: ["maxAttempts", "counterVar"],
  // Atendimento
  apply_tag: ["tagId", "tagName"],
  transfer_queue: ["queueId"],
  transfer_human: [],
  check_queue: [],
  check_agent_online: [],
  pause_bot: ["reason"],
  resume_bot: [],
  // IA
  ai_response: ["systemPrompt", "userPrompt", "model", "temperature", "maxTokens"],
  classify_ai: ["text", "intents", "context", "model", "storeAs"],
  extract_data_ai: ["text", "schema", "model", "storeAs"],
  summarize_ai: ["model", "storeAs"],
  // Integracoes
  http_request: ["url", "method", "headers", "body", "storeAs", "timeoutMs"],
  query_data: ["model", "findMode", "where", "limit", "storeAs"],
  generate_payment: ["gatewayId", "amount", "description", "storeAs"],
  check_payment: ["chargeId", "storeAs"],
  send_email: ["to", "subject", "body"],
  create_event: ["title", "startsAt", "endsAt", "userId"],
  notify_team: ["channel", "message"],
  // Relatorios
  log_event: ["event", "message"],
  nps_survey: ["question", "storeAs"]
};

// Defesa: em algumas combinacoes de Sequelize 5 + mysql2 + DataType.JSON,
// o backend retorna o campo como STRING JSON serializada em vez de
// objeto parseado. Parseamos aqui antes de aplicar o allow-list.
const parseIfString = raw => {
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

const sanitizeConfigJson = (type, raw) => {
  const parsed = parseIfString(raw);
  if (!parsed || typeof parsed !== "object") return {};
  const allowed = CONFIG_ALLOWED_KEYS[type] || [];
  const out = {};
  for (const k of allowed) {
    if (parsed[k] !== undefined) out[k] = parsed[k];
  }
  return out;
};

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexDirection: "column", height: "100vh" },
  appBar: { background: "#fff", color: theme.palette.text.primary, boxShadow: "0 1px 2px rgba(0,0,0,0.08)" },
  toolbar: { display: "flex", gap: theme.spacing(1) },
  title: { flex: 1, fontWeight: 600 },
  body: { display: "flex", flex: 1, minHeight: 0 },
  palette: {
    width: 240,
    borderRight: "1px solid #e0e0e0",
    background: "#fafafa",
    padding: theme.spacing(1.5),
    overflowY: "auto"
  },
  paletteTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    letterSpacing: 0.6,
    marginBottom: theme.spacing(0.5),
    textTransform: "uppercase"
  },
  paletteItem: {
    border: "1px solid #e0e0e0",
    borderRadius: 6,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
    cursor: "grab",
    background: "#fff",
    fontSize: 13,
    userSelect: "none",
    transition: "border-color 0.15s",
    "&:hover": { borderColor: "#1976d2" }
  },
  paletteSwatch: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 8,
    verticalAlign: "middle"
  },
  canvas: { flex: 1, minHeight: 0, position: "relative" },
  inspector: {
    width: 320,
    borderLeft: "1px solid #e0e0e0",
    background: "#fafafa",
    padding: theme.spacing(2),
    overflowY: "auto"
  },
  inspectorEmpty: {
    color: "#999",
    fontSize: 13,
    textAlign: "center",
    marginTop: theme.spacing(6)
  },
  field: { marginTop: theme.spacing(1.5) },
  loading: { display: "flex", justifyContent: "center", padding: 64 }
}));

const flowNodeStyle = type => {
  const def = NODE_DEF_MAP[type] || { color: "#999", label: type };
  return {
    border: `2px solid ${def.color}`,
    background: "#fff",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    minWidth: 140,
    color: "#333"
  };
};

// Tipos que usam custom renderer no react-flow (handles dinamicos/
// rotulados). Demais usam "default" — 1 entrada, 1 saida.
const CUSTOM_NODE_TYPES = new Set(["switch", "condition"]);

const toReactFlowNodes = nodes =>
  (nodes || []).map(n => ({
    id: n.nodeKey,
    type: CUSTOM_NODE_TYPES.has(n.type) ? n.type : "default",
    position: { x: n.positionX || 0, y: n.positionY || 0 },
    data: {
      label: n.label || NODE_DEF_MAP[n.type]?.label || n.type,
      nodeType: n.type,
      // Sanitiza no load — se o banco tem lixo de saves antigos
      // (imagem base64 num campo, chaves descontinuadas, etc.) ele
      // some na primeira passagem pelo editor.
      configJson: sanitizeConfigJson(n.type, n.configJson || {})
    },
    // Custom nodes tem estilo proprio; demais usam o flowNodeStyle padrao.
    style: CUSTOM_NODE_TYPES.has(n.type) ? undefined : flowNodeStyle(n.type)
  }));

const toReactFlowEdges = edges =>
  (edges || []).map(e => ({
    id: `${e.sourceNodeKey}->${e.targetNodeKey}-${e.sourceHandle || ""}`,
    source: e.sourceNodeKey,
    target: e.targetNodeKey,
    sourceHandle: e.sourceHandle || undefined,
    label: e.sourceHandle || undefined,
    animated: false
  }));

const FlowEditorInner = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const { id } = useParams();
  const history = useHistory();
  const wrapperRef = useRef(null);
  // Acessa o store interno do react-flow pra ler nodes/edges sempre
  // atualizados (sem cair em closure stale do React). Vital no save:
  // queremos o snapshot pos-blur, nao o da render que criou handleSave.
  const reactFlowInstance = useReactFlow();
  // Handle imperativo exposto pelo NodeInspector — o pai chama
  // `inspectorRef.current.getCurrentValues()` no save pra puxar o que
  // esta atualmente nos inputs uncontrolled. Bypass total de closure
  // e callback-ref recriacao.
  const inspectorRef = useRef(null);

  const [flow, setFlow] = useState(null);
  const [queues, setQueues] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({
    message: "",
    isFirstContact: false,
    callOpenAi: false
  });
  const [testResult, setTestResult] = useState(null);
  const [testRunning, setTestRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [flowRes, queuesRes, tagsRes] = await Promise.all([
        api.get(`/ai-flows/${id}`),
        api.get("/queue"),
        api.get("/client-tags").catch(() => ({ data: [] }))
      ]);
      setFlow(flowRes.data);
      setQueues(queuesRes.data || []);
      setTags(tagsRes.data || []);
      setNodes(toReactFlowNodes(flowRes.data.AiFlowNodes || flowRes.data.nodes));
      setEdges(toReactFlowEdges(flowRes.data.AiFlowEdges || flowRes.data.edges));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [id, setNodes, setEdges]);

  useEffect(() => {
    load();
  }, [load]);

  const onConnect = useCallback(
    params => {
      setEdges(eds =>
        addEdge(
          { ...params, label: params.sourceHandle || undefined },
          eds
        )
      );
    },
    [setEdges]
  );

  const onDragOver = useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    event => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/aiflow-node");
      if (!type) return;
      if (type === "start") {
        if (nodes.some(n => n.data?.nodeType === "start")) {
          toast.error("Ja existe um node Inicio neste fluxo.");
          return;
        }
      }
      const bounds = wrapperRef.current.getBoundingClientRect();
      const position = {
        x: event.clientX - bounds.left - 100,
        y: event.clientY - bounds.top - 20
      };
      const newNode = {
        id: makeNodeKey(),
        type: CUSTOM_NODE_TYPES.has(type) ? type : "default",
        position,
        data: {
          label: NODE_DEF_MAP[type]?.label || type,
          nodeType: type,
          // Switch comeca com 2 opcoes vazias pra usuario editar
          configJson:
            type === "switch"
              ? {
                  source: "message",
                  options: [
                    { label: "Opcao 1", kind: "contains", value: "" },
                    { label: "Opcao 2", kind: "contains", value: "" }
                  ]
                }
              : {}
        },
        style: CUSTOM_NODE_TYPES.has(type) ? undefined : flowNodeStyle(type)
      };
      setNodes(nds => nds.concat(newNode));
    },
    [nodes, setNodes]
  );

  const handlePaletteDragStart = (event, type) => {
    event.dataTransfer.setData("application/aiflow-node", type);
    event.dataTransfer.effectAllowed = "move";
  };

  const onNodeClick = (_event, node) => {
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
  };
  const onPaneClick = () => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  };
  const onEdgeClick = (_event, edge) => {
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
  };

  // Filtra deletes: nao permite remover o node "start" (cancela a
  // operacao re-inserindo o node, mantendo o resto do delete).
  const onNodesDeleteSafe = useCallback(
    deleted => {
      const startBlocked = deleted.find(d => d.data?.nodeType === "start");
      if (startBlocked) {
        toast.error("Nao da pra remover o node Inicio.");
        // Re-insere o node start (foi removido pelo react-flow antes
        // deste callback rodar).
        setNodes(nds => {
          const stillThere = nds.some(n => n.id === startBlocked.id);
          if (stillThere) return nds;
          return [...nds, startBlocked];
        });
        // Tambem restaura edges que tocavam o start (foram apagadas em
        // cascade pelo react-flow quando o node sumiu)
        setEdges(eds => {
          const original = edgesRefForRestore.current;
          if (!original) return eds;
          return original;
        });
      }
      setDirty(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onEdgesDeleteSafe = useCallback(() => {
    setDirty(true);
    setSelectedEdgeId(null);
  }, []);

  // Guarda referencia atual de edges pra possivel restore quando o
  // user tentar deletar o Start.
  const edgesRefForRestore = useRef(null);
  useEffect(() => {
    edgesRefForRestore.current = edges;
  }, [edges]);

  const handleDeleteSelectedEdge = () => {
    if (!selectedEdgeId) return;
    setEdges(eds => eds.filter(e => e.id !== selectedEdgeId));
    setSelectedEdgeId(null);
    setDirty(true);
  };

  const selectedEdge = useMemo(
    () => edges.find(e => e.id === selectedEdgeId) || null,
    [edges, selectedEdgeId]
  );

  const selectedNode = useMemo(
    () => nodes.find(n => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const updateSelectedConfig = patch => {
    setDirty(true);
    setNodes(nds =>
      nds.map(n =>
        n.id === selectedNodeId
          ? {
              ...n,
              data: { ...n.data, configJson: { ...n.data.configJson, ...patch } }
            }
          : n
      )
    );
  };

  const updateSelectedLabel = label => {
    setDirty(true);
    setNodes(nds =>
      nds.map(n =>
        n.id === selectedNodeId ? { ...n, data: { ...n.data, label } } : n
      )
    );
  };

  const deleteSelected = () => {
    if (!selectedNodeId) return;
    if (selectedNode?.data?.nodeType === "start") {
      toast.error("Nao e possivel remover o node Inicio.");
      return;
    }
    setEdges(eds =>
      eds.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId)
    );
    setNodes(nds => nds.filter(n => n.id !== selectedNodeId));
    setSelectedNodeId(null);
  };

  const handleSave = async () => {
    // 1) Forca blur do input em foco — dispara onBlur que faz commit do
    //    state local no setNodes.
    if (
      document.activeElement &&
      typeof document.activeElement.blur === "function"
    ) {
      document.activeElement.blur();
    }
    // 2) Yield pra React processar os setStates do onBlur.
    await new Promise(r => setTimeout(r, 0));

    // 3) Le nodes/edges direto do store do react-flow.
    const currentNodes = reactFlowInstance.getNodes();
    const currentEdges = reactFlowInstance.getEdges();

    if (!currentNodes.some(n => n.data.nodeType === "start")) {
      toast.error("O fluxo precisa de um node Inicio.");
      return;
    }

    // 4) Pede ao NodeInspector os valores atuais dos inputs uncontrolled
    //    do nodo SELECIONADO (so um nodo pode estar no inspector por vez).
    //    A funcao le os refs estaveis (useRef objeto, nao callback) e
    //    devolve um patch — aplicamos no nodo certo.
    const inspectorValues = inspectorRef.current?.getCurrentValues?.();
    const enrichedNodes = currentNodes.map(n => {
      if (
        !inspectorValues ||
        inspectorValues.nodeId !== n.id ||
        !inspectorValues.fields
      ) {
        return n;
      }
      const cfg = { ...(n.data.configJson || {}) };
      const fields = inspectorValues.fields;
      let label = n.data.label;
      if (typeof fields.label === "string") label = fields.label;
      if (n.data.nodeType === "send_message") {
        if (typeof fields.text === "string") cfg.text = fields.text;
      }
      if (n.data.nodeType === "ai_response") {
        ["systemPrompt", "userPrompt", "model"].forEach(f => {
          if (typeof fields[f] === "string") cfg[f] = fields[f];
        });
      }
      if (n.data.nodeType === "condition") {
        if (typeof fields.value === "string") cfg.value = fields.value;
      }
      return { ...n, data: { ...n.data, label, configJson: cfg } };
    });

    setSaving(true);
    try {
      const payload = {
        nodes: enrichedNodes.map(n => {
          const cleanCfg = sanitizeConfigJson(
            n.data.nodeType,
            n.data.configJson || {}
          );
          return {
            nodeKey: n.id,
            type: n.data.nodeType,
            label: n.data.label,
            positionX: n.position.x,
            positionY: n.position.y,
            configJson: Object.keys(cleanCfg).length > 0 ? cleanCfg : null
          };
        }),
        edges: currentEdges.map(e => ({
          sourceNodeKey: e.source,
          targetNodeKey: e.target,
          sourceHandle: e.sourceHandle || null
        }))
      };

      // Sanity check pos-sanitizacao: se ainda houver algo > 500KB num
      // node, e porque a UI realmente conhece o campo (text gigante,
      // por exemplo) — avisa antes de tentar enviar.
      for (const n of payload.nodes) {
        if (!n.configJson) continue;
        const size = JSON.stringify(n.configJson).length;
        if (size > 512 * 1024) {
          toast.error(
            `Node "${n.label || n.type}" tem ${Math.round(
              size / 1024
            )} KB de configuracao — limpe o campo "Texto" antes de salvar.`
          );
          setSaving(false);
          return;
        }
      }
      // Log textual (nao colapsa no console) — facil de colar de volta.
      // eslint-disable-next-line no-console
      console.log(
        "[ai-flows] PUT /graph payload (texto):\n" +
          JSON.stringify(payload, null, 2)
      );
      const { data } = await api.put(`/ai-flows/${id}/graph`, payload);
      // eslint-disable-next-line no-console
      console.log(
        "[ai-flows] PUT /graph response (texto):\n" +
          JSON.stringify(data, null, 2)
      );
      toast.success(
        `Fluxo salvo: ${payload.nodes.length} nodes, ${payload.edges.length} edges.`,
        { autoClose: 4000 }
      );
      setDirty(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[ai-flows] save failed", err?.response?.data || err);
      if (err?.response?.status === 413) {
        toast.error(
          "Payload do fluxo passa do limite (25MB). Verifique se ha texto ou imagem gigante em algum node."
        );
      } else if (err?.response?.data?.error) {
        toast.error(`Erro ao salvar: ${err.response.data.error}`, {
          autoClose: 8000
        });
      } else {
        toastError(err);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      // Busca o estado REAL do banco (nao o local), pra confirmar o que
      // foi realmente persistido.
      const { data } = await api.get(`/ai-flows/${id}`);
      const exportable = {
        name: data.name,
        description: data.description,
        providerType: data.providerType,
        defaultModel: data.defaultModel,
        nodes: (data.AiFlowNodes || data.nodes || []).map(n => ({
          nodeKey: n.nodeKey,
          type: n.type,
          label: n.label,
          positionX: n.positionX,
          positionY: n.positionY,
          configJson: sanitizeConfigJson(n.type, n.configJson || {})
        })),
        edges: (data.AiFlowEdges || data.edges || []).map(e => ({
          sourceNodeKey: e.sourceNodeKey,
          targetNodeKey: e.targetNodeKey,
          sourceHandle: e.sourceHandle || null
        }))
      };
      const blob = new Blob([JSON.stringify(exportable, null, 2)], {
        type: "application/json"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ai-flow-${id}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(
        `JSON do banco baixado (${exportable.nodes.length} nodes).`
      );
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTemplates = async () => {
    setTemplatesOpen(true);
    if (templates.length === 0) {
      setTemplatesLoading(true);
      try {
        const { data } = await api.get("/ai-flows/templates");
        setTemplates(data || []);
      } catch (err) {
        toastError(err);
      } finally {
        setTemplatesLoading(false);
      }
    }
  };

  const handleApplyTemplate = async tplKey => {
    const ok = await confirm({
      title: "Aplicar este template?",
      message:
        "Todos os nodes e edges do fluxo atual são substituídos pelos do template. Não dá para desfazer.",
      confirmLabel: "Substituir fluxo"
    });
    if (!ok) return;
    setApplyingTemplate(tplKey);
    try {
      await api.post(`/ai-flows/${id}/apply-template`, {
        templateKey: tplKey
      });
      toast.success("Template aplicado. Recarregando fluxo...");
      // Recarrega o fluxo do banco — pega nodes/edges novos
      await load();
      setTemplatesOpen(false);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast.error(`Erro: ${err.response.data.error}`);
      } else {
        toastError(err);
      }
    } finally {
      setApplyingTemplate(null);
    }
  };

  const handleImportApply = () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch (err) {
      toast.error("JSON invalido.");
      return;
    }
    const rawNodes = Array.isArray(parsed.nodes) ? parsed.nodes : [];
    const rawEdges = Array.isArray(parsed.edges) ? parsed.edges : [];
    if (rawNodes.length === 0) {
      toast.error("JSON precisa ter ao menos 1 node.");
      return;
    }
    const newNodes = rawNodes.map((n, i) => ({
      id: n.nodeKey || makeNodeKey(),
      type: "default",
      position: {
        x: typeof n.positionX === "number" ? n.positionX : 100 + i * 40,
        y: typeof n.positionY === "number" ? n.positionY : 100 + i * 80
      },
      data: {
        label: n.label || NODE_DEF_MAP[n.type]?.label || n.type,
        nodeType: n.type,
        configJson: sanitizeConfigJson(n.type, n.configJson || {})
      },
      style: flowNodeStyle(n.type)
    }));
    const idByKey = newNodes.reduce((acc, n) => {
      acc[n.id] = true;
      return acc;
    }, {});
    const newEdges = rawEdges
      .filter(e => idByKey[e.sourceNodeKey] && idByKey[e.targetNodeKey])
      .map(e => ({
        id: `${e.sourceNodeKey}->${e.targetNodeKey}-${e.sourceHandle || ""}`,
        source: e.sourceNodeKey,
        target: e.targetNodeKey,
        sourceHandle: e.sourceHandle || undefined,
        label: e.sourceHandle || undefined
      }));
    setNodes(newNodes);
    setEdges(newEdges);
    setDirty(true);
    setImportOpen(false);
    setImportText("");
    toast.success(
      `${newNodes.length} nodes / ${newEdges.length} edges carregados. Clique em Salvar para persistir.`
    );
  };

  const handleRunTest = async () => {
    if (!testForm.message.trim()) {
      toast.error("Informe a mensagem de teste.");
      return;
    }
    setTestRunning(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/ai-flows/${id}/test`, {
        message: testForm.message,
        simulate: {
          isFirstContact: testForm.isFirstContact,
          callOpenAi: testForm.callOpenAi
        }
      });
      setTestResult(data);
    } catch (err) {
      toastError(err);
    } finally {
      setTestRunning(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      const { data } = await api.post(`/ai-flows/${id}/toggle`);
      setFlow(prev => ({ ...prev, ...data }));
      toast.success(
        data.status === "active" ? "Fluxo ativado." : "Fluxo desativado."
      );
    } catch (err) {
      toastError(err);
    }
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  if (!flow) return null;

  return (
    <div className={classes.root}>
      {confirmDialog}
      <AppBar position="static" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <IconButton onClick={() => history.push("/ai-flows")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.title}>
            {flow.name}{" "}
            <span style={{ fontWeight: 400, color: "#888", fontSize: 13 }}>
              ({flow.status})
            </span>
          </Typography>
          <Tooltip title="Exportar JSON do banco (verifica o que esta realmente persistido)">
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudDownloadOutlinedIcon />}
              onClick={handleExport}
            >
              Exportar
            </Button>
          </Tooltip>
          <Tooltip title="Importar JSON (substitui graph atual; salvar persiste)">
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadOutlinedIcon />}
              onClick={() => setImportOpen(true)}
            >
              Importar
            </Button>
          </Tooltip>
          <Tooltip title="Aplicar template pronto (substitui graph)">
            <Button
              variant="outlined"
              size="small"
              startIcon={<LibraryBooksOutlinedIcon />}
              onClick={handleOpenTemplates}
            >
              Templates
            </Button>
          </Tooltip>
          <Tooltip title="Historico de execucoes">
            <Button
              variant="outlined"
              size="small"
              startIcon={<HistoryOutlinedIcon />}
              onClick={() => history.push(`/ai-flows/${id}/executions`)}
            >
              Logs
            </Button>
          </Tooltip>
          <Tooltip title="Testar fluxo">
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={() => {
                setTestResult(null);
                setTestOpen(true);
              }}
            >
              Testar
            </Button>
          </Tooltip>
          <Tooltip
            title={flow.status === "active" ? "Desativar fluxo" : "Ativar fluxo"}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<PowerSettingsNewIcon />}
              onClick={handleToggleStatus}
            >
              {flow.status === "active" ? "Desativar" : "Ativar"}
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<SaveOutlinedIcon />}
            onClick={handleSave}
            disabled={saving}
            style={
              dirty
                ? { boxShadow: "0 0 0 2px #ff5252", animation: "pulse 1.5s infinite" }
                : undefined
            }
          >
            {saving ? "Salvando..." : dirty ? "Salvar*" : "Salvar"}
          </Button>
        </Toolbar>
      </AppBar>

      <div className={classes.body}>
        <div className={classes.palette}>
          <PaletteCategorized
            classes={classes}
            onDragStart={handlePaletteDragStart}
          />
        </div>

        <div className={classes.canvas} ref={wrapperRef}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onEdgeClick={onEdgeClick}
            onNodesDelete={onNodesDeleteSafe}
            onEdgesDelete={onEdgesDeleteSafe}
            onDragOver={onDragOver}
            onDrop={onDrop}
            deleteKeyCode={["Delete", "Backspace"]}
            fitView
          >
            <Background gap={16} />
            <Controls />
            <MiniMap
              nodeStrokeColor={n => NODE_DEF_MAP[n.data?.nodeType]?.color || "#999"}
              nodeColor={n => NODE_DEF_MAP[n.data?.nodeType]?.color || "#999"}
            />
          </ReactFlow>
        </div>

        {selectedEdge ? (
          <div className={classes.inspector}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Conexao selecionada
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Ligacao entre dois blocos do fluxo.
            </Typography>
            <Paper
              variant="outlined"
              style={{ padding: 12, marginTop: 12, fontSize: 12 }}
            >
              <div>
                <strong>De:</strong> {selectedEdge.source}
              </div>
              <div>
                <strong>Para:</strong> {selectedEdge.target}
              </div>
              {selectedEdge.sourceHandle && (
                <div>
                  <strong>Handle:</strong> {selectedEdge.sourceHandle}
                </div>
              )}
            </Paper>
            <Divider style={{ margin: "16px 0" }} />
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDeleteSelectedEdge}
              fullWidth
            >
              Remover conexao
            </Button>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ display: "block", marginTop: 8 }}
            >
              Atalho: selecione e tecle Delete ou Backspace.
            </Typography>
          </div>
        ) : (
          <NodeInspector
            ref={inspectorRef}
            classes={classes}
            node={selectedNode}
            queues={queues}
            tags={tags}
            onLabelChange={updateSelectedLabel}
            onConfigChange={updateSelectedConfig}
            onDelete={deleteSelected}
          />
        )}
      </div>

      <Dialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Templates de fluxo prontos</DialogTitle>
        <DialogContent>
          {templatesLoading ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <CircularProgress />
            </div>
          ) : templates.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhum template disponivel.
            </Typography>
          ) : (
            templates.map(tpl => (
              <Paper
                key={tpl.key}
                variant="outlined"
                style={{ padding: 12, marginBottom: 12 }}
              >
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  {tpl.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginTop: 4, marginBottom: 8 }}
                >
                  {tpl.description}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={applyingTemplate === tpl.key}
                  onClick={() => handleApplyTemplate(tpl.key)}
                >
                  {applyingTemplate === tpl.key
                    ? "Aplicando..."
                    : "Aplicar este template"}
                </Button>
              </Paper>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplatesOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Importar fluxo (JSON)</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="textSecondary">
            Cole um JSON com nodes (e edges opcional). Tipos validos:
            start, send_message, ai_response, condition, apply_tag,
            transfer_queue, transfer_human, end. Exemplo minimo:
          </Typography>
          <pre
            style={{
              fontSize: 11,
              background: "#f5f5f5",
              padding: 8,
              borderRadius: 4,
              overflow: "auto",
              marginTop: 8,
              marginBottom: 8
            }}
          >
{`{
  "nodes": [
    { "nodeKey": "n1", "type": "start", "positionX": 100, "positionY": 100 },
    { "nodeKey": "n2", "type": "send_message", "label": "Boas-vindas",
      "positionX": 100, "positionY": 220,
      "configJson": { "text": "Seja bem vindo, {{name}}!" } },
    { "nodeKey": "n3", "type": "end",
      "positionX": 100, "positionY": 340 }
  ],
  "edges": [
    { "sourceNodeKey": "n1", "targetNodeKey": "n2" },
    { "sourceNodeKey": "n2", "targetNodeKey": "n3" }
  ]
}`}
          </pre>
          <TextField
            multiline
            minRows={10}
            value={importText}
            onChange={e => {
              const value = e.target.value;
              setImportText(value);
            }}
            fullWidth
            variant="outlined"
            placeholder="Cole o JSON aqui"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleImportApply}
            variant="contained"
            color="primary"
          >
            Carregar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={testOpen}
        onClose={() => setTestOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Testar fluxo</DialogTitle>
        <DialogContent>
          <TextField
            label="Mensagem do cliente"
            value={testForm.message}
            onChange={e => {
              const value = e.target.value;
              setTestForm(prev => ({ ...prev, message: value }));
            }}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            autoFocus
          />
          <FormControlLabel
            style={{ marginTop: 12 }}
            control={
              <Switch
                checked={testForm.isFirstContact}
                onChange={e => {
                  const checked = e.target.checked;
                  setTestForm(prev => ({ ...prev, isFirstContact: checked }));
                }}
                color="primary"
              />
            }
            label="Simular como primeiro contato"
          />
          <FormControlLabel
            style={{ marginTop: 0, display: "block" }}
            control={
              <Switch
                checked={testForm.callOpenAi}
                onChange={e => {
                  const checked = e.target.checked;
                  setTestForm(prev => ({ ...prev, callOpenAi: checked }));
                }}
                color="primary"
              />
            }
            label="Chamar OpenAI de verdade (consome tokens)"
          />

          {testResult && (
            <Paper variant="outlined" style={{ marginTop: 16, padding: 12 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Resultado: <Chip size="small" label={testResult.endedAs} />
              </Typography>
              {testResult.steps.map((s, i) => (
                <div
                  key={`${s.nodeKey}-${i}`}
                  style={{
                    borderLeft: `3px solid ${
                      s.error ? "#c62828" : "#1976d2"
                    }`,
                    paddingLeft: 8,
                    marginBottom: 8
                  }}
                >
                  <div style={{ fontSize: 13 }}>
                    <strong>{i + 1}. {s.nodeType}</strong>
                    {s.label ? ` — ${s.label}` : ""}
                  </div>
                  {s.output && (
                    <pre
                      style={{
                        fontSize: 11,
                        background: "#f5f5f5",
                        padding: 6,
                        borderRadius: 4,
                        overflow: "auto",
                        margin: "4px 0 0"
                      }}
                    >
                      {JSON.stringify(s.output, null, 2)}
                    </pre>
                  )}
                  {s.error && (
                    <div style={{ color: "#c62828", fontSize: 12 }}>
                      Erro: {s.error}
                    </div>
                  )}
                </div>
              ))}
              {Object.keys(testResult.finalVars || {}).length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Typography variant="caption" color="textSecondary">
                    Variaveis finais:
                  </Typography>
                  <pre
                    style={{
                      fontSize: 11,
                      background: "#f5f5f5",
                      padding: 6,
                      borderRadius: 4,
                      overflow: "auto"
                    }}
                  >
                    {JSON.stringify(testResult.finalVars, null, 2)}
                  </pre>
                </div>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestOpen(false)}>Fechar</Button>
          <Button
            onClick={handleRunTest}
            variant="contained"
            color="primary"
            disabled={testRunning}
          >
            {testRunning ? "Rodando..." : "Rodar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const NodeInspector = forwardRef((props, ref) => {
  const {
    classes,
    node,
    queues,
    tags,
    onLabelChange,
    onConfigChange,
    onDelete
  } = props;

  // Refs estaveis — objetos persistentes que sobrevivem a re-renders.
  // O `inputRef` do MUI TextField popula o `.current` desses refs com
  // o <input> nativo. Como sao refs OBJETO (nao callback ref), nao
  // ha desregistro/re-registro a cada render — o ref permanece valido.
  const labelRef = useRef(null);
  const textRef = useRef(null);
  const askQuestionRef = useRef(null);
  const askStoreAsRef = useRef(null);
  const systemPromptRef = useRef(null);
  const userPromptRef = useRef(null);
  const modelRef = useRef(null);
  const condValueRef = useRef(null);

  // Expoe getCurrentValues pro pai. O pai chama no save pra ler o
  // estado atual dos inputs uncontrolled. Retorna nodeId pra o pai
  // ter certeza que esta aplicando no nodo certo.
  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => {
        if (!node) return null;
        return {
          nodeId: node.id,
          nodeType: node.data?.nodeType,
          fields: {
            label: labelRef.current?.value,
            text: textRef.current?.value,
            question: askQuestionRef.current?.value,
            storeAs: askStoreAsRef.current?.value,
            systemPrompt: systemPromptRef.current?.value,
            userPrompt: userPromptRef.current?.value,
            model: modelRef.current?.value,
            value: condValueRef.current?.value
          }
        };
      }
    }),
    [node?.id]
  );

  if (!node) {
    return (
      <div className={classes.inspector}>
        <div className={classes.inspectorEmpty}>
          Clique em um bloco para editar.
        </div>
      </div>
    );
  }
  const cfg = node.data.configJson || {};
  const type = node.data.nodeType;
  const def = NODE_DEF_MAP[type];

  return (
    <div className={classes.inspector}>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
        {def?.label || type}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {def?.description}
      </Typography>

      {/* UNCONTROLLED: defaultValue + inputRef OBJETO estavel.
          key={node.id} forca remount quando troca de nodo selecionado,
          recarregando defaultValue. Pai chama getCurrentValues() no
          save pra puxar os valores atuais. */}
      <TextField
        key={`${node.id}-label`}
        label="Titulo (opcional)"
        defaultValue={node.data.label || ""}
        inputRef={labelRef}
        fullWidth
        variant="outlined"
        size="small"
        className={classes.field}
      />

      {type === "send_message" && (
        <TextField
          key={`${node.id}-text`}
          label="Texto"
          defaultValue={cfg.text || ""}
          inputRef={textRef}
          fullWidth
          variant="outlined"
          size="small"
          multiline
          minRows={4}
          className={classes.field}
          helperText="variaveis: {{name}}, {{client.firstName}}, {{message}}, {{vars.NOME}}"
        />
      )}

      {type === "ask" && (
        <>
          <TextField
            key={`${node.id}-question`}
            label="Pergunta"
            defaultValue={cfg.question || ""}
            inputRef={askQuestionRef}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            className={classes.field}
            helperText="Pergunta enviada ao cliente. O fluxo PARA e aguarda a proxima mensagem."
          />
          <TextField
            key={`${node.id}-storeAs`}
            label="Salvar resposta na variavel"
            defaultValue={cfg.storeAs || ""}
            inputRef={askStoreAsRef}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText='Ex: "nome", "cpf", "opcao". Usavel via {{vars.nome}} em mensagens seguintes ou em condition com source="vars.nome".'
          />
        </>
      )}

      {type === "ai_response" && (
        <>
          <TextField
            key={`${node.id}-sysp`}
            label="System prompt"
            defaultValue={cfg.systemPrompt || ""}
            inputRef={systemPromptRef}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            className={classes.field}
            helperText="Comportamento da IA (persona, regras, limites)"
          />
          <TextField
            key={`${node.id}-userp`}
            label="User prompt (template)"
            defaultValue={cfg.userPrompt || ""}
            inputRef={userPromptRef}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            className={classes.field}
            helperText="Vazio = usa a mensagem do cliente. Suporta {{message}}, {{client.firstName}}"
          />
          <TextField
            key={`${node.id}-model`}
            label="Modelo"
            defaultValue={cfg.model || ""}
            inputRef={modelRef}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Vazio = usa o modelo padrao do fluxo"
          />
          <TextField
            label="Temperatura"
            type="number"
            inputProps={{ step: 0.1, min: 0, max: 2 }}
            value={cfg.temperature ?? 0.4}
            onChange={e => {
              const value = Number(e.target.value);
              onConfigChange({ temperature: value });
            }}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
        </>
      )}

      {type === "switch" && (
        <SwitchInspector
          classes={classes}
          node={node}
          onConfigChange={onConfigChange}
        />
      )}

      {type === "wait" && (
        <TextField
          key={`${node.id}-delayMs`}
          label="Aguardar (ms)"
          defaultValue={cfg.delayMs ?? 2000}
          type="number"
          onBlur={e => {
            const v = Number(e.target.value);
            onConfigChange({ delayMs: Math.max(0, Math.min(v, 30000)) });
          }}
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
          helperText="Maximo 30000 ms (30s) — pra nao prender a engine."
        />
      )}

      {type === "business_hours" && (
        <>
          <TextField
            key={`${node.id}-startHour`}
            label="Hora abertura (0-23)"
            defaultValue={cfg.startHour ?? 9}
            type="number"
            inputProps={{ min: 0, max: 23 }}
            onBlur={e => onConfigChange({ startHour: Number(e.target.value) })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            key={`${node.id}-endHour`}
            label="Hora fechamento (0-23)"
            defaultValue={cfg.endHour ?? 18}
            type="number"
            inputProps={{ min: 0, max: 23 }}
            onBlur={e => onConfigChange({ endHour: Number(e.target.value) })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            key={`${node.id}-days`}
            label="Dias (0=dom, 6=sab)"
            defaultValue={(cfg.days || [1, 2, 3, 4, 5]).join(",")}
            onBlur={e => {
              const arr = String(e.target.value)
                .split(",")
                .map(s => Number(s.trim()))
                .filter(n => Number.isFinite(n) && n >= 0 && n <= 6);
              onConfigChange({ days: arr });
            }}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText='Ex: "1,2,3,4,5" para seg-sex. Saidas: open / closed.'
          />
        </>
      )}

      {type === "send_media" && (
        <>
          <TextField
            key={`${node.id}-mediaUrl`}
            label="URL da midia"
            defaultValue={cfg.mediaUrl || ""}
            onBlur={e => onConfigChange({ mediaUrl: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Implementacao real do upload de media ainda esta pendente; por enquanto envia caption + URL."
          />
          <TextField
            key={`${node.id}-mediaType`}
            label="Tipo (image/audio/video/document)"
            defaultValue={cfg.mediaType || "image"}
            onBlur={e => onConfigChange({ mediaType: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            key={`${node.id}-caption`}
            label="Caption"
            defaultValue={cfg.caption || ""}
            onBlur={e => onConfigChange({ caption: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            className={classes.field}
          />
        </>
      )}

      {type === "check_variable" && (
        <>
          <TextField
            key={`${node.id}-varName`}
            label="Nome da variavel"
            defaultValue={cfg.varName || ""}
            onBlur={e => onConfigChange({ varName: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Ex: 'nome', 'cpf' — sem 'vars.' na frente."
          />
          <TextField
            select
            label="Tipo"
            value={cfg.kind || "equals"}
            onChange={e => onConfigChange({ kind: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          >
            <MenuItem value="equals">Igual a</MenuItem>
            <MenuItem value="contains">Contem</MenuItem>
            <MenuItem value="not_empty">Nao vazio</MenuItem>
            <MenuItem value="is_empty">Vazio</MenuItem>
          </TextField>
          <TextField
            key={`${node.id}-cv-value`}
            label="Valor"
            defaultValue={cfg.value || ""}
            onBlur={e => onConfigChange({ value: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Saidas: true / false"
          />
        </>
      )}

      {type === "validate_response" && (
        <>
          <TextField
            key={`${node.id}-pattern`}
            label="Pattern (regex)"
            defaultValue={cfg.pattern || ""}
            onBlur={e => onConfigChange({ pattern: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText='Ex: "^\\d{11}$" pra CPF. Saidas: valid / invalid.'
          />
          <TextField
            key={`${node.id}-vr-source`}
            label="Origem"
            defaultValue={cfg.source || "message"}
            onBlur={e => onConfigChange({ source: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText='"message" ou "vars.NOME"'
          />
        </>
      )}

      {type === "pause_bot" && (
        <TextField
          key={`${node.id}-reason`}
          label="Motivo (opcional)"
          defaultValue={cfg.reason || ""}
          onBlur={e => onConfigChange({ reason: e.target.value })}
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
          helperText="Salvo em ClientAiSetting.blockedReason"
        />
      )}

      {type === "classify_ai" && (
        <>
          <TextField
            key={`${node.id}-ci-text`}
            label="Texto pra classificar"
            defaultValue={cfg.text || "{{message}}"}
            onBlur={e => onConfigChange({ text: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            className={classes.field}
          />
          <TextField
            key={`${node.id}-ci-intents`}
            label="Intencoes (uma por linha)"
            defaultValue={(cfg.intents || []).join("\n")}
            onBlur={e => {
              const arr = String(e.target.value)
                .split("\n")
                .map(s => s.trim())
                .filter(Boolean);
              onConfigChange({ intents: arr });
            }}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={4}
            className={classes.field}
            helperText="Cada intencao vira um handle de saida. IA escolhe uma."
          />
          <TextField
            key={`${node.id}-ci-storeAs`}
            label="Salvar em variavel"
            defaultValue={cfg.storeAs || "lastIntent"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
        </>
      )}

      {type === "http_request" && (
        <>
          <TextField
            key={`${node.id}-url`}
            label="URL"
            defaultValue={cfg.url || ""}
            onBlur={e => onConfigChange({ url: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Suporta {{vars.NOME}} no template."
          />
          <TextField
            select
            label="Metodo"
            value={cfg.method || "GET"}
            onChange={e => onConfigChange({ method: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="PATCH">PATCH</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </TextField>
          <TextField
            key={`${node.id}-hr-body`}
            label="Body (JSON ou texto)"
            defaultValue={cfg.body || ""}
            onBlur={e => onConfigChange({ body: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            className={classes.field}
          />
          <TextField
            key={`${node.id}-hr-storeAs`}
            label="Salvar resposta em"
            defaultValue={cfg.storeAs || "httpResponse"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Saidas: success / error"
          />
        </>
      )}

      {type === "query_data" && (
        <>
          <TextField
            select
            label="Modelo"
            value={cfg.model || ""}
            onChange={e => onConfigChange({ model: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          >
            <MenuItem value="">Selecione</MenuItem>
            <MenuItem value="client">client — Cliente</MenuItem>
            <MenuItem value="contact">contact — Contato</MenuItem>
            <MenuItem value="ticket">ticket — Ticket</MenuItem>
            <MenuItem value="deal">deal — Deal (negocio)</MenuItem>
            <MenuItem value="product">product — Produto</MenuItem>
            <MenuItem value="client_status">client_status — Status</MenuItem>
            <MenuItem value="queue">queue — Fila</MenuItem>
          </TextField>
          <TextField
            select
            label="Modo"
            value={cfg.findMode || "one"}
            onChange={e => onConfigChange({ findMode: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="findOne (um registro) ou findAll (lista)."
          >
            <MenuItem value="one">findOne</MenuItem>
            <MenuItem value="all">findAll</MenuItem>
          </TextField>
          <TextField
            key={`${node.id}-qd-where`}
            label="Where (JSON)"
            defaultValue={
              cfg.where
                ? typeof cfg.where === "string"
                  ? cfg.where
                  : JSON.stringify(cfg.where, null, 2)
                : '{\n  "email": "{{vars.email}}"\n}'
            }
            onBlur={e => {
              const raw = e.target.value;
              try {
                const parsed = JSON.parse(raw);
                onConfigChange({ where: parsed });
              } catch {
                onConfigChange({ where: {} });
              }
            }}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            className={classes.field}
            helperText='Objeto JSON com filtros Sequelize. Suporta {{vars.NOME}} em strings.'
          />
          {cfg.findMode === "all" && (
            <TextField
              key={`${node.id}-qd-limit`}
              label="Limit (max 50)"
              defaultValue={cfg.limit ?? 10}
              type="number"
              inputProps={{ min: 1, max: 50 }}
              onBlur={e =>
                onConfigChange({
                  limit: Math.min(Math.max(Number(e.target.value) || 1, 1), 50)
                })
              }
              fullWidth
              variant="outlined"
              size="small"
              className={classes.field}
            />
          )}
          <TextField
            key={`${node.id}-qd-storeAs`}
            label="Salvar resultado em"
            defaultValue={cfg.storeAs || "queryResult"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Saidas: found / not_found. Use {{vars.NOME.campo}} depois."
          />
        </>
      )}

      {(type === "send_options" || type === "menu_interactive") && (
        <>
          <TextField
            key={`${node.id}-so-text`}
            label="Texto antes das opcoes"
            defaultValue={cfg.text || "Escolha uma opcao:"}
            onBlur={e => onConfigChange({ text: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            className={classes.field}
          />
          <TextField
            key={`${node.id}-so-opts`}
            label="Opcoes (uma por linha)"
            defaultValue={
              Array.isArray(cfg.options)
                ? cfg.options
                    .map(o => (typeof o === "string" ? o : o?.label || ""))
                    .join("\n")
                : ""
            }
            onBlur={e => {
              const arr = String(e.target.value)
                .split("\n")
                .map(s => s.trim())
                .filter(Boolean)
                .map(label => ({ label }));
              onConfigChange({ options: arr });
            }}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={4}
            className={classes.field}
            helperText="Cada linha vira uma opcao numerada (1, 2, 3, ...)."
          />
          <TextField
            key={`${node.id}-so-storeAs`}
            label="Salvar resposta em"
            defaultValue={cfg.storeAs || "lastOption"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Combine com switch (source=vars.NOME) pra rotear."
          />
        </>
      )}

      {type === "retry_limit" && (
        <>
          <TextField
            label="Maximo de tentativas"
            defaultValue={cfg.maxAttempts ?? 3}
            type="number"
            inputProps={{ min: 1, max: 99 }}
            onBlur={e =>
              onConfigChange({ maxAttempts: Number(e.target.value) || 3 })
            }
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Variavel contador"
            defaultValue={cfg.counterVar || "__retryCount"}
            onBlur={e => onConfigChange({ counterVar: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Saidas: ok / exceeded."
          />
        </>
      )}

      {type === "timeout_response" && (
        <TextField
          label="Timeout (ms)"
          defaultValue={cfg.timeoutMs ?? 60000}
          type="number"
          onBlur={e => onConfigChange({ timeoutMs: Number(e.target.value) || 60000 })}
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
          helperText="Marca janela em vars.__timeouts[nodeKey]."
        />
      )}

      {type === "extract_data_ai" && (
        <>
          <TextField
            key={`${node.id}-ed-text`}
            label="Texto"
            defaultValue={cfg.text || "{{message}}"}
            onBlur={e => onConfigChange({ text: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            className={classes.field}
          />
          <TextField
            key={`${node.id}-ed-schema`}
            label="Schema (descricao em texto)"
            defaultValue={cfg.schema || "{ nome: string, cpf: string }"}
            onBlur={e => onConfigChange({ schema: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            className={classes.field}
            helperText="IA retorna JSON. Saidas: success / error."
          />
          <TextField
            key={`${node.id}-ed-storeAs`}
            label="Salvar JSON em"
            defaultValue={cfg.storeAs || "extracted"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
        </>
      )}

      {type === "summarize_ai" && (
        <>
          <TextField
            label="Quantas msgs incluir"
            defaultValue={cfg.limit ?? 12}
            type="number"
            inputProps={{ min: 1, max: 50 }}
            onBlur={e => onConfigChange({ limit: Number(e.target.value) || 12 })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Salvar em"
            defaultValue={cfg.storeAs || "summary"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
        </>
      )}

      {type === "generate_payment" && (
        <>
          <TextField
            label="Gateway ID"
            defaultValue={cfg.gatewayId || ""}
            type="number"
            onBlur={e => onConfigChange({ gatewayId: Number(e.target.value) })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="ID do PaymentGateway cadastrado em /payment-gateways."
          />
          <TextField
            label="Valor (R$)"
            defaultValue={cfg.amount || ""}
            type="number"
            onBlur={e => onConfigChange({ amount: Number(e.target.value) })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Descricao"
            defaultValue={cfg.description || ""}
            onBlur={e => onConfigChange({ description: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Salvar charge em"
            defaultValue={cfg.storeAs || "lastCharge"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Saidas: success / error."
          />
        </>
      )}

      {type === "check_payment" && (
        <TextField
          label="Charge ID"
          defaultValue={cfg.chargeId || ""}
          type="number"
          onBlur={e => onConfigChange({ chargeId: Number(e.target.value) })}
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
          helperText="Saidas: paid / pending / failed / expired / other / error."
        />
      )}

      {type === "send_email" && (
        <>
          <TextField
            label="Para (email)"
            defaultValue={cfg.to || ""}
            onBlur={e => onConfigChange({ to: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Assunto"
            defaultValue={cfg.subject || ""}
            onBlur={e => onConfigChange({ subject: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Corpo"
            defaultValue={cfg.body || ""}
            onBlur={e => onConfigChange({ body: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={3}
            className={classes.field}
            helperText="Registrado em AuditLog (SMTP nao integrado ainda)."
          />
        </>
      )}

      {type === "create_event" && (
        <>
          <TextField
            label="Titulo"
            defaultValue={cfg.title || ""}
            onBlur={e => onConfigChange({ title: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="UserId responsavel"
            defaultValue={cfg.userId || ""}
            type="number"
            onBlur={e => onConfigChange({ userId: Number(e.target.value) })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Default: createdBy do fluxo."
          />
          <TextField
            label="Tipo (task/meeting/reminder)"
            defaultValue={cfg.kind || "task"}
            onBlur={e => onConfigChange({ kind: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Inicio (ISO ou template)"
            defaultValue={cfg.startsAt || ""}
            onBlur={e => onConfigChange({ startsAt: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Default: agora + 1h."
          />
        </>
      )}

      {type === "notify_team" && (
        <>
          <TextField
            label="Canal"
            defaultValue={cfg.channel || "notification"}
            onBlur={e => onConfigChange({ channel: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            label="Mensagem"
            defaultValue={cfg.message || ""}
            onBlur={e => onConfigChange({ message: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            className={classes.field}
            helperText="Broadcast socket.io + AuditLog."
          />
        </>
      )}

      {type === "nps_survey" && (
        <>
          <TextField
            label="Pergunta"
            defaultValue={
              cfg.question ||
              "De 0 a 10, o quanto voce recomendaria nosso atendimento?"
            }
            onBlur={e => onConfigChange({ question: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            className={classes.field}
          />
          <TextField
            label="Salvar score em"
            defaultValue={cfg.storeAs || "npsScore"}
            onBlur={e => onConfigChange({ storeAs: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Saidas: promoter (9-10) / passive (7-8) / detractor (0-6) / invalid."
          />
        </>
      )}

      {type === "subflow" && (
        <TextField
          label="Flow ID alvo"
          defaultValue={cfg.targetFlowId || ""}
          type="number"
          onBlur={e => onConfigChange({ targetFlowId: Number(e.target.value) })}
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
          helperText="O flow alvo precisa estar ativo e configurado para a mesma fila/whatsapp."
        />
      )}

      {type === "log_event" && (
        <>
          <TextField
            key={`${node.id}-event`}
            label="Nome do evento"
            defaultValue={cfg.event || "AI_FLOW_EVENT"}
            onBlur={e => onConfigChange({ event: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          />
          <TextField
            key={`${node.id}-le-message`}
            label="Mensagem"
            defaultValue={cfg.message || ""}
            onBlur={e => onConfigChange({ message: e.target.value })}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            className={classes.field}
            helperText="Salvo em AuditLog. Suporta {{vars.NOME}}."
          />
        </>
      )}

      {type === "condition" && (
        <>
          <TextField
            select
            label="Tipo da condicao"
            value={cfg.kind || "contains"}
            onChange={e => {
              const value = e.target.value;
              onConfigChange({ kind: value });
            }}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
          >
            <MenuItem value="contains">Contem texto</MenuItem>
            <MenuItem value="equals">Igual a</MenuItem>
            <MenuItem value="ai_intent">Intencao da IA igual a</MenuItem>
            <MenuItem value="client_has_tag">Cliente tem a tag</MenuItem>
          </TextField>
          <TextField
            label="Origem do valor"
            value={cfg.source || "message"}
            onChange={e => {
              const value = e.target.value;
              onConfigChange({ source: value });
            }}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText='"message" = ultima mensagem do cliente. "vars.NOME" = variavel salva por um node Perguntar.'
          />
          <TextField
            key={`${node.id}-value`}
            label="Valor"
            defaultValue={cfg.value || ""}
            inputRef={condValueRef}
            fullWidth
            variant="outlined"
            size="small"
            className={classes.field}
            helperText="Conectar saidas com handles 'true' e 'false'"
          />
        </>
      )}

      {type === "apply_tag" && (
        <TextField
          select
          label="Tag"
          value={cfg.tagId || ""}
          onChange={e => {
            const value = e.target.value;
            onConfigChange({ tagId: value });
          }}
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
        >
          <MenuItem value="">Selecione</MenuItem>
          {tags.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      {type === "transfer_queue" && (
        <TextField
          select
          label="Fila destino"
          value={cfg.queueId || ""}
          onChange={e => {
            const value = e.target.value;
            onConfigChange({ queueId: value });
          }}
          fullWidth
          variant="outlined"
          size="small"
          className={classes.field}
        >
          <MenuItem value="">Selecione</MenuItem>
          {queues.map(q => (
            <MenuItem key={q.id} value={q.id}>
              {q.name}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Divider style={{ margin: "16px 0" }} />

      <Button
        variant="outlined"
        color="secondary"
        size="small"
        startIcon={<DeleteOutlineIcon />}
        onClick={onDelete}
        fullWidth
        disabled={type === "start"}
      >
        Remover bloco
      </Button>
    </div>
  );
});

/**
 * Palette agrupada por categoria. Cada grupo eh colapsavel (state local
 * por grupo) com persistencia no localStorage do estado aberto/fechado.
 * Default: tudo expandido na primeira abertura.
 */
const PaletteCategorized = ({ classes, onDragStart }) => {
  const STORAGE_KEY = "ai-flows-palette-open-v1";
  const [open, setOpen] = React.useState(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {
      /* ignore */
    }
    return NODE_CATEGORIES.reduce((acc, c) => {
      acc[c.key] = true;
      return acc;
    }, {});
  });
  React.useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(open));
    } catch {
      /* ignore */
    }
  }, [open]);

  const toggle = key => setOpen(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <>
      <div className={classes.paletteTitle}>Blocos</div>
      {NODE_CATEGORIES.map(cat => {
        const items = NODE_DEFINITIONS.filter(d => d.category === cat.key);
        if (items.length === 0) return null;
        const isOpen = open[cat.key] !== false;
        return (
          <div key={cat.key} style={{ marginBottom: 8 }}>
            <div
              onClick={() => toggle(cat.key)}
              style={{
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                color: "#555",
                padding: "4px 0",
                userSelect: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                letterSpacing: 0.5,
                textTransform: "uppercase"
              }}
            >
              <span>{cat.label}</span>
              <span style={{ fontSize: 10 }}>{isOpen ? "▾" : "▸"}</span>
            </div>
            {isOpen &&
              items.map(def => (
                <div
                  key={def.type}
                  className={classes.paletteItem}
                  draggable
                  onDragStart={e => onDragStart(e, def.type)}
                >
                  <span
                    className={classes.paletteSwatch}
                    style={{ background: def.color }}
                  />
                  <strong>{def.label}</strong>
                  <div
                    style={{ fontSize: 11, color: "#888", marginTop: 2 }}
                  >
                    {def.description}
                  </div>
                </div>
              ))}
          </div>
        );
      })}
    </>
  );
};

/**
 * Inspector dedicado ao switch — gerencia lista dinamica de opcoes.
 * Diferente dos outros campos de texto livre, aqui usa state controlado
 * porque o tamanho da lista muda (add/remove). Cada mudanca dispara
 * onConfigChange imediatamente — sem race porque opcoes sao editadas
 * fora do fluxo de uncontrolled refs do NodeInspector pai.
 */
const SwitchInspector = ({ classes, node, onConfigChange }) => {
  const cfg = node?.data?.configJson || {};
  const source = cfg.source || "message";
  const options = Array.isArray(cfg.options) ? cfg.options : [];

  const writeOptions = nextOptions => {
    onConfigChange({ options: nextOptions });
  };

  const updateOption = (idx, patch) => {
    const next = options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    writeOptions(next);
  };

  const addOption = () => {
    writeOptions([
      ...options,
      { label: `Opcao ${options.length + 1}`, kind: "contains", value: "" }
    ]);
  };

  const removeOption = idx => {
    if (options.length <= 1) return;
    writeOptions(options.filter((_o, i) => i !== idx));
  };

  const moveOption = (idx, direction) => {
    const target = idx + direction;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[idx], next[target]] = [next[target], next[idx]];
    writeOptions(next);
  };

  return (
    <>
      <TextField
        label="Origem do valor"
        value={source}
        onChange={e => {
          const value = e.target.value;
          onConfigChange({ source: value });
        }}
        fullWidth
        variant="outlined"
        size="small"
        className={classes.field}
        helperText='"message" ou "vars.NOME". A primeira opcao que casa vence.'
      />
      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: "block", marginTop: 12, marginBottom: 4 }}
      >
        Opcoes ({options.length}) — saidas output_0..output_{options.length - 1} + default
      </Typography>
      {options.map((opt, i) => (
        <div
          key={`opt-${i}`}
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: 6,
            padding: 8,
            marginBottom: 8,
            background: "#fafafa"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              marginBottom: 6
            }}
          >
            <span
              style={{
                fontSize: 10,
                color: "#666",
                fontWeight: 600,
                flex: 1
              }}
            >
              output_{i}
            </span>
            <IconButton
              size="small"
              disabled={i === 0}
              onClick={() => moveOption(i, -1)}
              title="Mover pra cima"
            >
              <span style={{ fontSize: 12 }}>↑</span>
            </IconButton>
            <IconButton
              size="small"
              disabled={i === options.length - 1}
              onClick={() => moveOption(i, +1)}
              title="Mover pra baixo"
            >
              <span style={{ fontSize: 12 }}>↓</span>
            </IconButton>
            <IconButton
              size="small"
              disabled={options.length <= 1}
              onClick={() => removeOption(i)}
              title="Remover opcao"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </div>
          <TextField
            label="Rotulo"
            value={opt.label || ""}
            onChange={e => {
              const value = e.target.value;
              updateOption(i, { label: value });
            }}
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginBottom: 6 }}
          />
          <TextField
            select
            label="Tipo"
            value={opt.kind || "contains"}
            onChange={e => {
              const value = e.target.value;
              updateOption(i, { kind: value });
            }}
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginBottom: 6 }}
          >
            <MenuItem value="contains">Contem</MenuItem>
            <MenuItem value="equals">Igual a</MenuItem>
            <MenuItem value="ai_intent">Intencao IA</MenuItem>
            <MenuItem value="client_has_tag">Cliente tem tag</MenuItem>
          </TextField>
          <TextField
            label="Valor"
            value={opt.value || ""}
            onChange={e => {
              const value = e.target.value;
              updateOption(i, { value });
            }}
            fullWidth
            variant="outlined"
            size="small"
          />
        </div>
      ))}
      <Button
        variant="outlined"
        size="small"
        onClick={addOption}
        fullWidth
        style={{ marginBottom: 8 }}
      >
        + Adicionar opcao
      </Button>
      <Typography
        variant="caption"
        color="textSecondary"
        style={{ display: "block" }}
      >
        Conecte cada handle do node a um destino. Se nenhuma opcao casar,
        flui pelo handle "default" (ultimo, em cinza).
      </Typography>
    </>
  );
};

const FlowEditor = () => (
  <ReactFlowProvider>
    <FlowEditorInner />
  </ReactFlowProvider>
);

export default FlowEditor;
